/**
 * Performance Monitoring Utilities
 *
 * Web Vitals tracking (LCP, FID, CLS) and performance measurement.
 */

/**
 * Web Vital metric types
 */
export type WebVitalName = 'LCP' | 'FID' | 'CLS' | 'FCP' | 'TTFB' | 'INP';

/**
 * Web Vital metric data
 */
export interface WebVitalMetric {
  name: WebVitalName;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
  entries: PerformanceEntry[];
}

/**
 * Performance metric thresholds (in milliseconds, except CLS)
 */
const THRESHOLDS: Record<WebVitalName, { good: number; poor: number }> = {
  LCP: { good: 2500, poor: 4000 },      // Largest Contentful Paint
  FID: { good: 100, poor: 300 },        // First Input Delay
  CLS: { good: 0.1, poor: 0.25 },       // Cumulative Layout Shift (unitless)
  FCP: { good: 1800, poor: 3000 },      // First Contentful Paint
  TTFB: { good: 800, poor: 1800 },      // Time to First Byte
  INP: { good: 200, poor: 500 },        // Interaction to Next Paint
};

/**
 * Get rating for a metric value
 */
function getRating(
  name: WebVitalName,
  value: number
): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name];
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Generate unique metric ID
 */
function generateId(): string {
  return `v1-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Metric callback type
 */
type MetricCallback = (metric: WebVitalMetric) => void;

/**
 * Stored callbacks for metrics
 */
const metricCallbacks: MetricCallback[] = [];

/**
 * Register a callback for all Web Vitals metrics
 */
export function onWebVitals(callback: MetricCallback): void {
  metricCallbacks.push(callback);
}

/**
 * Report a metric to all registered callbacks
 */
function reportMetric(metric: WebVitalMetric): void {
  metricCallbacks.forEach((callback) => {
    try {
      callback(metric);
    } catch (error) {
      console.error('Error in metric callback:', error);
    }
  });
}

/**
 * Observe Largest Contentful Paint (LCP)
 */
export function observeLCP(): void {
  if (typeof PerformanceObserver !== 'function') return;

  let lcpValue = 0;
  let lcpEntries: PerformanceEntry[] = [];

  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    const lastEntry = entries[entries.length - 1] as PerformanceEntry & {
      startTime: number;
    };

    if (lastEntry) {
      lcpValue = lastEntry.startTime;
      lcpEntries = entries;
    }
  });

  observer.observe({ type: 'largest-contentful-paint', buffered: true });

  // Report on page hide or visibility change
  const reportLCP = () => {
    if (lcpValue > 0) {
      reportMetric({
        name: 'LCP',
        value: lcpValue,
        rating: getRating('LCP', lcpValue),
        delta: lcpValue,
        id: generateId(),
        navigationType: getNavigationType(),
        entries: lcpEntries,
      });
    }
    observer.disconnect();
  };

  // Report when page becomes hidden
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      reportLCP();
    }
  });

  // Also report on pagehide for Safari
  window.addEventListener('pagehide', reportLCP);
}

/**
 * Observe First Input Delay (FID)
 */
export function observeFID(): void {
  if (typeof PerformanceObserver !== 'function') return;

  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries() as (PerformanceEntry & {
      processingStart: number;
      startTime: number;
    })[];

    entries.forEach((entry) => {
      const value = entry.processingStart - entry.startTime;
      reportMetric({
        name: 'FID',
        value,
        rating: getRating('FID', value),
        delta: value,
        id: generateId(),
        navigationType: getNavigationType(),
        entries: [entry],
      });
    });

    observer.disconnect();
  });

  observer.observe({ type: 'first-input', buffered: true });
}

/**
 * Observe Cumulative Layout Shift (CLS)
 */
export function observeCLS(): void {
  if (typeof PerformanceObserver !== 'function') return;

  let clsValue = 0;
  let clsEntries: PerformanceEntry[] = [];
  let sessionValue = 0;
  let sessionEntries: PerformanceEntry[] = [];

  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries() as (PerformanceEntry & {
      hadRecentInput: boolean;
      value: number;
    })[];

    entries.forEach((entry) => {
      // Only count layout shifts without recent user input
      if (!entry.hadRecentInput) {
        const firstSessionEntry = sessionEntries[0] as
          | (PerformanceEntry & { startTime: number })
          | undefined;
        const lastSessionEntry = sessionEntries[sessionEntries.length - 1] as
          | (PerformanceEntry & { startTime: number })
          | undefined;

        // Start a new session if this shift is 1s+ after the last one
        // or if the session is already 5s long
        if (
          sessionValue &&
          (entry.startTime - (lastSessionEntry?.startTime || 0) > 1000 ||
            entry.startTime - (firstSessionEntry?.startTime || 0) > 5000)
        ) {
          sessionValue = 0;
          sessionEntries = [];
        }

        sessionValue += entry.value;
        sessionEntries.push(entry);

        if (sessionValue > clsValue) {
          clsValue = sessionValue;
          clsEntries = [...sessionEntries];
        }
      }
    });
  });

  observer.observe({ type: 'layout-shift', buffered: true });

  // Report on page hide
  const reportCLS = () => {
    if (clsValue > 0) {
      reportMetric({
        name: 'CLS',
        value: clsValue,
        rating: getRating('CLS', clsValue),
        delta: clsValue,
        id: generateId(),
        navigationType: getNavigationType(),
        entries: clsEntries,
      });
    }
    observer.disconnect();
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      reportCLS();
    }
  });

  window.addEventListener('pagehide', reportCLS);
}

/**
 * Observe First Contentful Paint (FCP)
 */
export function observeFCP(): void {
  if (typeof PerformanceObserver !== 'function') return;

  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries() as (PerformanceEntry & {
      startTime: number;
    })[];
    const entry = entries.find((e) => e.name === 'first-contentful-paint');

    if (entry) {
      reportMetric({
        name: 'FCP',
        value: entry.startTime,
        rating: getRating('FCP', entry.startTime),
        delta: entry.startTime,
        id: generateId(),
        navigationType: getNavigationType(),
        entries: [entry],
      });
      observer.disconnect();
    }
  });

  observer.observe({ type: 'paint', buffered: true });
}

/**
 * Observe Time to First Byte (TTFB)
 */
export function observeTTFB(): void {
  if (typeof PerformanceObserver !== 'function') return;

  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries() as PerformanceNavigationTiming[];
    const navEntry = entries[0];

    if (navEntry) {
      const value = navEntry.responseStart - navEntry.requestStart;
      reportMetric({
        name: 'TTFB',
        value,
        rating: getRating('TTFB', value),
        delta: value,
        id: generateId(),
        navigationType: navEntry.type,
        entries: [navEntry],
      });
      observer.disconnect();
    }
  });

  observer.observe({ type: 'navigation', buffered: true });
}

/**
 * Get navigation type
 */
function getNavigationType(): string {
  if (performance.getEntriesByType) {
    const navEntry = performance.getEntriesByType(
      'navigation'
    )[0] as PerformanceNavigationTiming | undefined;
    return navEntry?.type || 'navigate';
  }
  return 'navigate';
}

/**
 * Initialize all Web Vitals observers
 */
export function initWebVitals(callback?: MetricCallback): void {
  if (callback) {
    onWebVitals(callback);
  }

  // Start observing all metrics
  observeLCP();
  observeFID();
  observeCLS();
  observeFCP();
  observeTTFB();
}

/**
 * Performance mark helper
 */
export function mark(name: string): void {
  if (performance.mark) {
    performance.mark(name);
  }
}

/**
 * Performance measure helper
 */
export function measure(
  name: string,
  startMark: string,
  endMark?: string
): PerformanceMeasure | null {
  if (performance.measure) {
    try {
      return performance.measure(name, startMark, endMark);
    } catch (error) {
      console.warn('Performance measure failed:', error);
    }
  }
  return null;
}

/**
 * Get all performance marks
 */
export function getMarks(): PerformanceEntry[] {
  if (performance.getEntriesByType) {
    return performance.getEntriesByType('mark');
  }
  return [];
}

/**
 * Get all performance measures
 */
export function getMeasures(): PerformanceEntry[] {
  if (performance.getEntriesByType) {
    return performance.getEntriesByType('measure');
  }
  return [];
}

/**
 * Clear performance marks and measures
 */
export function clearPerformanceData(): void {
  if (performance.clearMarks) {
    performance.clearMarks();
  }
  if (performance.clearMeasures) {
    performance.clearMeasures();
  }
}

/**
 * Get memory usage (Chrome only)
 */
export function getMemoryUsage(): {
  usedJSHeapSize?: number;
  totalJSHeapSize?: number;
  jsHeapSizeLimit?: number;
} | null {
  const memory = (performance as Performance & {
    memory?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
  }).memory;

  if (memory) {
    return {
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit,
    };
  }
  return null;
}

/**
 * Log performance summary to console
 */
export function logPerformanceSummary(): void {
  console.group('Performance Summary');

  const marks = getMarks();
  const measures = getMeasures();
  const memory = getMemoryUsage();

  if (marks.length > 0) {
    console.log('Marks:', marks.map((m) => ({ name: m.name, time: m.startTime })));
  }

  if (measures.length > 0) {
    console.log(
      'Measures:',
      measures.map((m) => ({ name: m.name, duration: m.duration }))
    );
  }

  if (memory) {
    console.log('Memory:', {
      used: `${(memory.usedJSHeapSize! / 1024 / 1024).toFixed(2)} MB`,
      total: `${(memory.totalJSHeapSize! / 1024 / 1024).toFixed(2)} MB`,
      limit: `${(memory.jsHeapSizeLimit! / 1024 / 1024).toFixed(2)} MB`,
    });
  }

  console.groupEnd();
}

export default {
  initWebVitals,
  onWebVitals,
  mark,
  measure,
  getMarks,
  getMeasures,
  clearPerformanceData,
  getMemoryUsage,
  logPerformanceSummary,
};
